import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from "react-router";
import Box from "@mui/material/Box";
import * as theme from '../../theme';
import { Backdrop, CircularProgress, Typography, Button } from '@mui/material';
import { formatPhone } from '../../functions/InputHelpers';
import { useAuth } from '../../contexts/AuthContext';
import ErrorDisplay from '../../components/ErrorDisplay';
import AccountCredentials from './components/AccountCredentials';
import PersonalBusinessInfo from './components/PersonalBusinessInfo';
import PaymentDetails from './components/PaymentDetails';
import ConsentTerms from './components/ConsentTerms';
import ReviewEnroll from './components/ReviewEnroll';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import getServerTimestamp from '../../functions/endpoints/GetServerTimestamp';
import { completeEnrollment } from './functions/enrollment';
import CostContext from '../../contexts/CostContext';

const functions = getFunctions();
const auth = getAuth();

export default function Enroll() {
  const [searchParams] = useSearchParams(); // Add this to read URL params
  const [promoCode, setPromoCode] = useState(null); // State for promo code
  const [discount, setDiscount] = useState(0); // State for discount amount
  const [promoError, setPromoError] = useState(''); // State for promo validation errors
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    password: '',
    confirmPassword: '',
    dob: null,
    street: '',
    address2: '',
    city: '',
    state: '',
    zip: ''
  });
  const [consentData, setConsentData] = useState({
    agreed: false,
    signature: ''
  });
  const [storeCardConsent, setStoreCardConsent] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [passwordStrength, setPasswordStrength] = useState('');
  const [passwordMatchError, setPasswordMatchError] = useState('');
  const [isStepValid, setIsStepValid] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [cardId, setCardId] = useState(null);
  const [cardDetails, setCardDetails] = useState(null);
  const [nonce, setNonce] = useState(null);
  const [nonceError, setNonceError] = useState(null);
  const [billingInfo, setBillingInfo] = useState({
    cardholderName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: ''
  });
  const navigate = useNavigate();
  const { setUserRole } = useAuth();

  const steps = ['Account Credentials', 'Personal & Business Info', 'Payment Details', 'Consent & Terms', 'Review & Enroll'];

  const clearError = () => setError("");

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'password') {
      updatePasswordStrength(value);
    } else if (name === 'confirmPassword') {
      updatePasswordMatch(value, formData.password);
    }
  }, [formData.password]);

  const updatePasswordStrength = useCallback((password) => {
    let strength = '';
    if (password.length >= 8 && /[a-z]/.test(password) && /[A-Z]/.test(password) && /\d/.test(password) && /[^a-zA-Z\d]/.test(password)) {
      strength = 'Strong';
    } else if (password.length >= 8 && (/[a-z]/.test(password) || /[A-Z]/.test(password)) && /\d/.test(password)) {
      strength = 'Medium';
    } else if (password.length >= 8) {
      strength = 'Weak';
    }
    setPasswordStrength(strength);
  }, []);

  const updatePasswordMatch = useCallback((confirm, password) => {
    setPasswordMatchError(password !== confirm ? 'Passwords do not match.' : '');
  }, []);

  const validateStep = useCallback((step) => {
    switch (step) {
      case 0:
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email address.';
        if (formData.password.length < 8) return 'Password must be at least 8 characters long.';
        if (formData.password !== formData.confirmPassword) return 'Passwords do not match.';
        return '';
      case 1:
        const requiredInfo = ['firstName', 'lastName', 'phone', 'dob', 'street', 'city', 'state', 'zip'];
        for (const field of requiredInfo) {
          if (!formData[field]) return `Missing required field: ${field}`;
        }
        if (formData.phone.length !== 12) return 'Please enter a valid phone number (e.g., 123-456-7890).';
        if (formData.zip.length !== 5 || !/^\d{5}$/.test(formData.zip)) return 'Please enter a valid 5-digit ZIP code.';
        return '';
      case 2:
        if (!customerId) return 'Customer setup incomplete. Please go back and try again.';
        if (!nonce && (!billingInfo.cardholderName || !billingInfo.addressLine1 || !billingInfo.city || !billingInfo.state || !billingInfo.zip)) {
          return 'Please provide valid payment details.';
        }
        if (!nonce && (billingInfo.zip.length !== 5 || !/^\d{5}$/.test(billingInfo.zip))) {
          return 'Please enter a valid 5-digit ZIP code for billing.';
        }
        if (!nonce) return 'Please verify your card details.';
        if (nonce && !storeCardConsent) return 'You must agree to store the card on file.';
        return '';
      case 3:
        if (!consentData.agreed) return 'You must agree to the Practitioner Agreement terms.';
        if (!consentData.signature) return 'Please provide your signature.';
        return '';
      case 4:
        return '';
      default:
        return '';
    }
  }, [formData, consentData, customerId, billingInfo, nonce, storeCardConsent]);

  useEffect(() => {
    const validationError = validateStep(activeStep);
    setIsStepValid(!validationError);
    if (!validationError && activeStep === 0) {
      setPasswordMatchError(formData.password !== formData.confirmPassword ? 'Passwords do not match.' : '');
    }
  }, [formData, consentData, customerId, billingInfo, nonce, storeCardConsent, activeStep, validateStep]);

  // New effect to validate promo code on mount
  useEffect(() => {
    const urlPromo = searchParams.get('promo');
    if (urlPromo) {
      setPromoCode(urlPromo);
      const validate = async () => {
        try {
          const validatePromoCode = httpsCallable(functions, 'validatePromoCode');
          const result = await validatePromoCode({ promoCode: urlPromo, incrementUsage: false });
          if (result.data.valid) {
            setDiscount(result.data.discount);
            setPromoError('');
          } else {
            setDiscount(0);
            setPromoError(result.data.message || 'Invalid promo code.');
          }
        } catch (err) {
          setDiscount(0);
          setPromoError('Failed to validate promo code. Proceeding with standard pricing.');
        }
      };
      validate();
    }
  }, [searchParams]);

  const handleNext = async () => {
    const validationError = validateStep(activeStep);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');

    if (activeStep === 1) {
      setLoading(true);
      try {
        const checkUserEmail = httpsCallable(functions, 'checkUserEmail');
        const emailCheck = await checkUserEmail({ email: formData.email.toLowerCase().trim() });
        if (emailCheck.data.exists) {
          setError('Email already in use. Please sign in instead.');
          setLoading(false);
          return;
        }

        const lookupOrCreateCustomer = httpsCallable(functions, 'lookupOrCreateCustomer');
        const result = await lookupOrCreateCustomer({
          email: formData.email.toLowerCase().trim(),
          firstName: formData.firstName,
          lastName: formData.lastName
        });
        setCustomerId(result.data.customerId);
        setBillingInfo({
          cardholderName: '',
          addressLine1: formData.street,
          addressLine2: formData.address2 || '',
          city: formData.city,
          state: formData.state,
          zip: formData.zip
        });
      } catch (err) {
        setError('Failed to process customer details: ' + err.message);
        setLoading(false);
        return;
      }
      setLoading(false);
    }

    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleTokenizeResponse = (token) => {
    if (token.status === 'OK') {
      setNonce(token.token);
      setNonceError(null);
      setStoreCardConsent(false); // Ensure checkbox is unchecked after successful tokenization
    } else {
      setNonceError('Failed to verify card: Please check details and try again.');
      console.error('Tokenization error details:', token.errors || token);
    }
  };

  const handleSaveEnrollmentCard = async () => {
    if (!nonce || !storeCardConsent) {
      setNonceError('Please agree to store the card on file.');
      return;
    }
    setLoading(true);
    try {
      const saveEnrollmentCard = httpsCallable(functions, 'saveEnrollmentCard');
      const result = await saveEnrollmentCard({
        nonce,
        customerId,
        cardholderName: billingInfo.cardholderName.trim(),
        billingAddress: {
          addressLine1: billingInfo.addressLine1.trim(),
          addressLine2: billingInfo.addressLine2.trim() || '',
          city: billingInfo.city.trim(),
          state: billingInfo.state,
          zip: billingInfo.zip.trim()
        }
      });
      setCardId(result.data.cardId);
      setCardDetails({
        brand: result.data.brand,
        last4: result.data.last4,
        expMonth: result.data.expMonth,
        expYear: result.data.expYear
      });
      setNonceError(null);
      setNonce(null);
      setStoreCardConsent(false);
      setActiveStep((prev) => prev + 1);
    } catch (err) {
      setNonceError('Failed to save card: ' + err.message);
    }
    setLoading(false);
  };

  const handleEnroll = async () => {
    const validationError = validateStep(activeStep);
    if (validationError) {
      setError(validationError);
      return;
    }
    setLoading(true);
    setError("");

    try {
      const timestamp = await getServerTimestamp();
      const processEnrollmentSubscription = httpsCallable(functions, 'processEnrollmentSubscription');
      const paymentResult = await processEnrollmentSubscription({
        customerId,
        cardId, 
        amount: CostContext.SubscriptionAmount,
        promoCode // Pass promoCode if present
      });
      if (paymentResult.data.status !== 'success') {
        throw new Error('Payment failed: ' + (paymentResult.data.error || 'Unknown error'));
      }
      const transId = paymentResult.data.transactionId;

      const normalizedEmail = formData.email.toLowerCase().trim();
      const authResult = await createUserWithEmailAndPassword(auth, normalizedEmail, formData.password);
      const user = authResult.user;

      await completeEnrollment({
        user,
        formData,
        consentData,
        timestamp,
        customerId,
        transId,
        paymentResult,
        cardDetails,
        cardId
      });

      await setUserRole(user);
      navigate('/dashboard');
    } catch (err) {
      console.error('Enrollment error:', err.message);
      setError(err.message || 'Failed to complete enrollment. Please try again.');
    }

    setLoading(false);
  };

  const practitionerName = useMemo(() => formData.businessName || `${formData.firstName} ${formData.lastName}`.trim(), [formData]);

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <AccountCredentials
            formData={formData}
            handleChange={handleChange}
            passwordStrength={passwordStrength}
            passwordMatchError={passwordMatchError}
            error={error}
            clearError={clearError}
          />
        );
      case 1:
        return (
          <PersonalBusinessInfo
            formData={formData}
            handleChange={handleChange}
            handleDateChange={(value) => setFormData(prev => ({ ...prev, dob: value }))}
            handlePhoneChange={(e) => {
              const input = e.target.value;
              if (/^[0-9-]*$/.test(input)) {
                const formatted = formatPhone(input);
                setFormData(prev => ({ ...prev, phone: formatted }));
              }
            }}
            handleStateChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
          />
        );
      case 2:
        return (
          <PaymentDetails
            billingInfo={billingInfo}
            handleBillingInfoChange={(e) => {
              const { name, value } = e.target;
              setBillingInfo(prev => ({ ...prev, [name]: value }));
            }}
            handleBillingStateChange={(e) => setBillingInfo(prev => ({ ...prev, state: e.target.value }))}
            nonce={nonce}
            nonceError={nonceError}
            setNonceError={setNonceError}
            handleTokenizeResponse={handleTokenizeResponse}
            isBillingValid={billingInfo.cardholderName && billingInfo.addressLine1 && billingInfo.city && billingInfo.state && billingInfo.zip && billingInfo.zip.length === 5 && /^\d{5}$/.test(billingInfo.zip)}
            storeCardConsent={storeCardConsent}
            handleStoreCardConsentChange={(e) => setStoreCardConsent(e.target.checked)}
          />
        );
      case 3:
        return (
          <ConsentTerms
            consentData={consentData}
            handleConsentChange={(e) => {
              const { name, value, checked, type } = e.target;
              setConsentData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
              }));
            }}
            practitionerName={practitionerName}
            formData={formData}
          />
        );
      case 4:
        return (
          <ReviewEnroll
            formData={formData}
            cardDetails={cardDetails}
            discount={discount} // Pass discount to ReviewEnroll
          />
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ ...theme.Container.basicWide, marginTop: 20 }}>
      <div style={theme.Heading}>Enroll in TruBalance</div>
      <div style={theme.SubHeadingText}>
        By completing the form below, you are enrolling in our practitioner partnership.
      </div>
      <div style={{ marginTop: activeStep > 0 && activeStep < 4 ? 15 : 0, marginBottom: 30, position: 'relative' }}>
        {(activeStep === 0 || activeStep === 4) && (
          <>
            <div style={theme.SubHeadingText}>
              This is a one-time setup fee, afterward you will have unlimited access to our program (per-scan fees still apply for client sessions).
            </div>
            <div style={{ ...theme.SubHeadingText, marginBottom: 33 }}>
              Once you've completed the enrollment process, you will be logged into our Dashboard, where you can begin training and work through your onboarding.
            </div>
          </>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h6">{steps[activeStep]}</Typography>
          <Box sx={{ px: 2, py: 1, borderRadius: 25, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            {activeStep + 1} of {steps.length}
          </Box>
        </Box>
        {error && (
          <Box sx={{ p: 2, borderRadius: 1, mb: 2 }}>
            <ErrorDisplay message={error} type="error" clearMessage={clearError} />
          </Box>
        )}
        {promoError && (
          <Box sx={{ p: 2, borderRadius: 1, mb: 2 }}>
            <ErrorDisplay message={promoError} type="warning" clearMessage={() => setPromoError('')} />
          </Box>
        )}
        {renderStepContent(activeStep)}
        <Box sx={{ display: 'flex', width: '100%', gap: 2, mt: 2, marginTop: 6 }}>
          {activeStep === 1 && (
            <Button
              variant="contained"
              color="secondary"
              sx={{ flex: 1 }}
              onClick={handleBack}
              disabled={loading}
            >
              Back
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            sx={{ flex: 1 }}
            onClick={activeStep === 2 && nonce ? handleSaveEnrollmentCard : activeStep === steps.length - 1 ? handleEnroll : handleNext}
            disabled={loading || !isStepValid}
          >
            {loading ? 'Processing...' : activeStep === 2 && nonce ? 'Save Card' : activeStep === steps.length - 1 ? 'Enroll & Charge Card' : 'Next'}
          </Button>
        </Box>
        <Backdrop
          sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, position: 'absolute', borderRadius: 1 }}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>
    </div>
  );
}